import { API_BASE } from "@/lib/apiBase";
import {
  SERVER_UPDATE_MAX_AGE_MS,
  SERVER_UPDATE_MESSAGE,
  interpretMaintenancePoll,
  type MaintenanceStatusBody,
} from "@/features/serverUpdate/maintenanceStatus";

/**
 * Gemeinsamer Zustand: Update-Anzeige während des API-Neustarts.
 * Caddy liefert /maintenance-status aus einer Datei, auch wenn uvicorn aus ist.
 */

const POLL_MS = 3000;
const LATCH_KEY = "eh-server-update";
const LATCH_SHOW_AFTER_MS = 400;

export interface ServerUpdateSnapshot {
  active: boolean;
  message: string;
}

const INACTIVE_SNAPSHOT: ServerUpdateSnapshot = {
  active: false,
  message: SERVER_UPDATE_MESSAGE,
};

type Listener = () => void;

let snapshot: ServerUpdateSnapshot = INACTIVE_SNAPSHOT;
const listeners = new Set<Listener>();
let watchers = 0;
let pollTimer: number | null = null;
let pollInFlight = false;
let latchedPending = false;
let latchTimer: number | null = null;
let unreachableTimer: number | null = null;
let reachableTimer: number | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

function readLatch(now = Date.now()): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    const raw = localStorage.getItem(LATCH_KEY);
    if (!raw) return false;
    const started = Date.parse(raw);
    if (!Number.isFinite(started)) return false;
    return now - started <= SERVER_UPDATE_MAX_AGE_MS;
  } catch {
    return false;
  }
}

function writeLatch(startedAt: string | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(LATCH_KEY, startedAt || new Date().toISOString());
  } catch {
    /* private mode */
  }
}

function clearLatch(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(LATCH_KEY);
  } catch {
    /* private mode */
  }
}

function setSnapshot(next: ServerUpdateSnapshot): void {
  if (snapshot.active === next.active && snapshot.message === next.message) return;
  snapshot = next;
  if (next.active) writeLatch(new Date().toISOString());
  else clearLatch();
  emit();
}

export function getServerUpdateSnapshot(): ServerUpdateSnapshot {
  return snapshot;
}

export function getServerUpdateServerSnapshot(): ServerUpdateSnapshot {
  return INACTIVE_SNAPSHOT;
}

export function isServerUpdateActive(): boolean {
  return snapshot.active;
}

export function subscribeServerUpdate(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function statusUrl(): string {
  const stamp = Date.now();
  const path = `/maintenance-status?t=${stamp}`;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

async function pollOnce(): Promise<void> {
  if (pollInFlight || typeof fetch === "undefined") return;
  pollInFlight = true;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 4000);
  try {
    const res = await fetch(statusUrl(), {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      signal: controller.signal,
    });
    let body: MaintenanceStatusBody | null = null;
    if (res.status === 200) {
      try {
        body = (await res.json()) as MaintenanceStatusBody;
      } catch {
        body = null;
      }
    }
    applyOutcome(interpretMaintenancePoll(res.status, body));
  } catch {
    applyOutcome(interpretMaintenancePoll(0, null));
  } finally {
    window.clearTimeout(timeoutId);
    pollInFlight = false;
  }
}

function applyOutcome(outcome: ReturnType<typeof interpretMaintenancePoll>): void {
  if (outcome.kind === "keep") return;
  latchedPending = false;
  if (latchTimer != null) {
    window.clearTimeout(latchTimer);
    latchTimer = null;
  }
  if (outcome.kind === "active") {
    setSnapshot({ active: true, message: outcome.message });
    if (outcome.startedAt) writeLatch(outcome.startedAt);
    return;
  }
  clearLatch();
  setSnapshot({ active: false, message: SERVER_UPDATE_MESSAGE });
}

function scheduleLatchReveal(): void {
  if (!readLatch() || snapshot.active || latchTimer != null) return;
  latchedPending = true;
  latchTimer = window.setTimeout(() => {
    latchTimer = null;
    if (!latchedPending) return;
    latchedPending = false;
    if (readLatch()) {
      setSnapshot({ active: true, message: SERVER_UPDATE_MESSAGE });
    }
  }, LATCH_SHOW_AFTER_MS);
}

/** Sofort anzeigen, z. B. nach der WebSocket-Ankündigung direkt vor dem Neustart. */
export function markServerUpdateActive(message?: string): void {
  latchedPending = false;
  if (latchTimer != null) {
    window.clearTimeout(latchTimer);
    latchTimer = null;
  }
  setSnapshot({
    active: true,
    message: (message || "").trim() || SERVER_UPDATE_MESSAGE,
  });
}

/** API-Call ist am Netz gescheitert: Status sofort nachziehen. */
export function noteApiUnreachable(): void {
  if (typeof window === "undefined") return;
  if (unreachableTimer != null) return;
  unreachableTimer = window.setTimeout(() => {
    unreachableTimer = null;
    void pollOnce();
  }, 200);
}

/** API antwortet wieder: Anzeige ausblenden, sobald die Statusdatei weg ist. */
export function noteApiReachable(): void {
  if (!snapshot.active || typeof window === "undefined") return;
  if (reachableTimer != null) return;
  reachableTimer = window.setTimeout(() => {
    reachableTimer = null;
    void pollOnce();
  }, 300);
}

export function startServerUpdateWatch(): () => void {
  if (typeof window === "undefined") return () => {};
  watchers += 1;
  if (watchers === 1) {
    scheduleLatchReveal();
    void pollOnce();
    pollTimer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void pollOnce();
    }, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
  }
  return stopServerUpdateWatch;
}

function onVisible(): void {
  if (document.visibilityState === "visible") void pollOnce();
}

function stopServerUpdateWatch(): void {
  watchers = Math.max(0, watchers - 1);
  if (watchers > 0) return;
  if (pollTimer != null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
  document.removeEventListener("visibilitychange", onVisible);
}
